import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import componentStyles from './sic-video-placeholder.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';

// --- Consent state singleton ---
// All component instances share one dataLayer listener so the push override
// is set up exactly once, and it properly chains any earlier override (e.g.
// the one set up by google_tag.js) rather than discarding it.

type ConsentCallback = () => void;
const _consentCallbacks = new Set<ConsentCallback>();

function _checkDataLayerForConsent(): boolean {
  const dl = window.dataLayer;
  if (!Array.isArray(dl)) return false;
  return dl.some(
    item =>
      item?.event === 'cookie_consent_marketing' ||
      item?.event === 'cookie_consent_all'
  );
}

function _setupConsentListener(): void {
  if (window._sicVideoConsentListening) return;
  window._sicVideoConsentListening = true;

  // Ensure dataLayer exists.
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [] as unknown as typeof window.dataLayer;
  }
  // Non-null assertion is safe: we just guaranteed it exists above.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const dataLayer = window.dataLayer!;

  // Capture the CURRENT push (may already be overridden by google_tag.js).
  const prevPush = dataLayer.push.bind(dataLayer);

  dataLayer.push = function (...items: Record<string, unknown>[]): number {
    const result = prevPush(...items);
    for (const item of items) {
      if (
        item?.event === 'cookie_consent_marketing' ||
        item?.event === 'cookie_consent_all'
      ) {
        _consentCallbacks.forEach(cb => cb());
      }
    }
    return result;
  };
}

// --- Component ---

/**
 * The SIC VideoPlaceholder component.
 *
 * Gates video iframe content behind Monsido marketing cookie consent.
 * Place the iframe inside a <template> child element; the component will
 * promote it to the DOM once the user has accepted marketing cookies.
 *
 * @element sic-video-placeholder
 * @slot placeholder - Custom placeholder UI (overrides the default message).
 * @slot default    - Populated automatically when consent is given.
 *
 * @example
 * <sic-video-placeholder>
 *   <template>
 *     <iframe src="https://..." title="Video title"></iframe>
 *   </template>
 * </sic-video-placeholder>
 */
@customElement('sic-video-placeholder')
export class VideoPlaceholder extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @state() private hasConsent = false;

  private readonly _onConsent: ConsentCallback = () => {
    this.hasConsent = true;
  };

  connectedCallback(): void {
    super.connectedCallback();
    // Check if consent was already given (e.g. returning visitor whose
    // Monsido event re-fired before this component connected).
    this.hasConsent = _checkDataLayerForConsent();
    _setupConsentListener();
    _consentCallbacks.add(this._onConsent);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    _consentCallbacks.delete(this._onConsent);
  }

  updated(changedProps: Map<string, unknown>): void {
    super.updated(changedProps);
    if (changedProps.has('hasConsent') && this.hasConsent) {
      this._promoteTemplate();
    }
  }

  /** Moves <template> content into the light DOM so the iframe loads. */
  private _promoteTemplate(): void {
    const tpl = this.querySelector<HTMLTemplateElement>(':scope > template');
    if (!tpl) return;
    this.appendChild(tpl.content.cloneNode(true));
    tpl.remove();
  }

  private _openConsentManager(): void {
    const manager = window.monsidoConsentManager;
    if (manager?.showBanner) {
      // 'cookie-preferences' skips the initial accept/reject prompt.
      manager.showBanner('cookie-preferences');
    }
  }

  render(): TemplateResult {
    if (this.hasConsent) {
      return html`<slot></slot>`;
    }

    return html`
      <div
        class="placeholder-wrapper"
        role="region"
        aria-label="Cookie consent required"
      >
        <slot name="placeholder">
          <div class="placeholder-content">
            <p class="placeholder-message">
              To view this content, please accept marketing cookies.
            </p>
            <button
              class="placeholder-button"
              type="button"
              @click=${this._openConsentManager}
            >
              Update Cookie Preferences
            </button>
          </div>
        </slot>
      </div>
    `;
  }
}

declare global {
  interface Window {
    _sicVideoConsentListening?: boolean;
    dataLayer?: Array<Record<string, unknown>> & {
      push: (...items: Record<string, unknown>[]) => number;
    };
    monsidoConsentManager?: { showBanner?: (screen?: string) => void };
  }
  interface HTMLElementTagNameMap {
    'sic-video-placeholder': VideoPlaceholder;
  }
}
